<template>
  <Teleport to="body">
    <div class="add-steps" role="dialog" aria-modal="true" :aria-label="$t('addLanguage.title')">
      <header class="steps-header">
        <button
          class="back-btn"
          data-test="steps-back"
          :aria-label="currentStep === 1 ? $t('common.close') : $t('addLanguage.back')"
          @click="goBack"
        >
          <font-awesome-icon :icon="currentStep === 1 ? 'fa-solid fa-xmark' : 'fa-solid fa-chevron-left'" />
        </button>
        <div class="step-dots" aria-hidden="true">
          <span v-for="s in 3" :key="s" :class="['dot', { active: s <= currentStep }]"></span>
        </div>
        <span class="header-spacer"></span>
      </header>

      <Transition :name="stepTransition" mode="out-in">
        <div :key="currentStep" class="step-body">
          <template v-if="currentStep === 1">
            <h2 class="step-title">{{ $t('onboarding.newLanguage.selectLanguage') }}</h2>
            <LanguagePickerGrid
              :selected-code="pickedLanguage"
              :disabled-base-codes="nativeLanguageCode ? [nativeLanguageCode] : []"
              :disabled-codes="heldCodes"
              :active-code="activeCode"
              :show-other-card="false"
              @pick="onPickLanguage"
            />
          </template>

          <template v-else-if="currentStep === 2">
            <h2 class="step-title">{{ $t('onboarding.newLanguage.whyLearning') }}</h2>
            <div class="chip-options">
              <button
                v-for="reason in reasons"
                :key="reason.key"
                :class="['chip-option', { active: pickedReason === reason.key }]"
                :data-test="'reason-' + reason.key"
                @click="$emit('pick-reason', reason.key)"
              >
                {{ reason.label }}
              </button>
            </div>
          </template>

          <template v-else>
            <h2 class="step-title">{{ $t('onboarding.newLanguage.proficiencyLevel') }}</h2>
            <div class="chip-options">
              <button
                v-for="level in proficiencyLevels"
                :key="level.key"
                :class="['chip-option', { active: pickedProficiency === level.key }]"
                :data-test="'proficiency-' + level.key"
                @click="$emit('pick-proficiency', level.key)"
              >
                {{ level.label }}
              </button>
            </div>
          </template>
        </div>
      </Transition>

      <footer class="steps-footer">
        <div v-if="errorKey" class="alert alert-danger" data-test="steps-error">
          {{ $t(errorKey) }}
        </div>
        <button
          v-if="currentStep > 1"
          class="cta-btn"
          data-test="steps-cta"
          :disabled="!stepComplete || isSubmitting"
          @click="onCta"
        >
          <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
          {{ currentStep === 2 ? $t('common.continue') : $t('addLanguage.submit') }}
        </button>
      </footer>
    </div>
  </Teleport>
</template>

<script>
import { ref as vueRef, computed, onUnmounted } from "vue";
import LanguagePickerGrid from "@/components/LanguagePickerGrid.vue";

// Full-screen stepped add-language flow (mobile): one decision per screen,
// progress dots, pinned CTA. The desktop presentation is the modal in
// AddLanguageFlow.vue; both share the useAddLanguageForm state, which the
// host owns — this component only renders it and emits picks.
export default {
  name: "AddLanguageSteps",
  components: { LanguagePickerGrid },
  props: {
    pickedLanguage: { type: String, default: null },
    pickedReason: { type: String, default: null },
    pickedProficiency: { type: String, default: null },
    reasons: { type: Array, required: true },
    proficiencyLevels: { type: Array, required: true },
    nativeLanguageCode: { type: String, default: null },
    heldCodes: { type: Array, default: () => [] },
    activeCode: { type: String, default: null },
    errorKey: { type: String, default: null },
    isSubmitting: { type: Boolean, default: false },
  },
  emits: ["close", "submit", "pick-language", "pick-reason", "pick-proficiency"],
  setup(props, { emit }) {
    const currentStep = vueRef(1);
    const stepTransition = vueRef("step-next");

    const stepComplete = computed(() => {
      if (currentStep.value === 2) return !!props.pickedReason;
      if (currentStep.value === 3) return !!props.pickedProficiency;
      return !!props.pickedLanguage;
    });

    const goNext = () => {
      stepTransition.value = "step-next";
      currentStep.value = Math.min(currentStep.value + 1, 3);
    };

    const goBack = () => {
      if (currentStep.value === 1) {
        emit("close");
        return;
      }
      stepTransition.value = "step-prev";
      currentStep.value -= 1;
    };

    // Auto-advance once the choice is visibly selected.
    let advanceTimer = null;
    const onPickLanguage = (code) => {
      emit("pick-language", code);
      clearTimeout(advanceTimer);
      advanceTimer = setTimeout(goNext, 150);
    };
    onUnmounted(() => clearTimeout(advanceTimer));

    const onCta = () => {
      if (currentStep.value === 2) {
        goNext();
      } else {
        emit("submit");
      }
    };

    return { currentStep, stepTransition, stepComplete, goBack, onPickLanguage, onCta };
  },
};
</script>

<style scoped>
.add-steps {
  position: fixed;
  inset: 0;
  /* Above the navigation layer (sidebar 2002, hamburger 2100), matching
     Modal.vue, so breakpoint drift can never leave nav above the flow. */
  z-index: 2150;
  display: flex;
  flex-direction: column;
  background: var(--al-surface-page, var(--al-surface));
  padding-top: max(8px, env(safe-area-inset-top, 0px));
}

.steps-header {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 8px 12px;
}

.back-btn {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 50%;
  color: var(--al-text);
  font-size: 18px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.back-btn:active {
  background: var(--al-surface-sunken);
}

.step-dots {
  flex: 1;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--al-border);
  transition: background-color var(--al-dur-medium, 300ms) var(--al-ease-move, ease);
}

.dot.active {
  background: var(--al-primary);
}

.header-spacer {
  width: 44px;
}

.step-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 16px 16px;
}

.step-title {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.24;
  color: var(--al-text);
  margin: 8px 0 16px;
}

.chip-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip-option {
  display: inline-flex;
  align-items: center;
  min-height: 48px;
  padding: 8px 16px;
  background-color: var(--al-surface-sunken);
  border: 2px solid transparent;
  border-radius: var(--al-radius-pill, 999px);
  font-size: 16px;
  color: var(--al-text);
  cursor: pointer;
  transition: all var(--al-dur-short, 180ms) var(--al-ease-move, ease);
  -webkit-tap-highlight-color: transparent;
}

.chip-option.active {
  background-color: var(--al-primary-soft);
  border-color: var(--al-primary);
  font-weight: 600;
}

.steps-footer {
  flex-shrink: 0;
  padding: 12px 16px max(16px, env(safe-area-inset-bottom, 0px));
}

.cta-btn {
  width: 100%;
  min-height: 52px;
  background-color: var(--al-primary);
  color: var(--al-white, #fff);
  border: none;
  border-radius: var(--al-radius, 12px);
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color var(--al-dur-short, 180ms) var(--al-ease-move, ease);
}

.cta-btn:active:not(:disabled) {
  background-color: var(--al-primary-hover);
}

.cta-btn:disabled {
  background-color: var(--al-light-blue, var(--al-border));
  cursor: not-allowed;
}

.alert {
  margin-bottom: 12px;
}

/* Horizontal step transitions; reduced motion falls back to fade. */
.step-next-enter-active,
.step-next-leave-active,
.step-prev-enter-active,
.step-prev-leave-active {
  transition: transform 260ms var(--al-ease-move, cubic-bezier(0.4, 0, 0.2, 1)),
    opacity 260ms var(--al-ease-move, cubic-bezier(0.4, 0, 0.2, 1));
}

.step-next-enter-from,
.step-prev-leave-to {
  transform: translateX(32px);
  opacity: 0;
}

.step-prev-enter-from,
.step-next-leave-to {
  transform: translateX(-32px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .step-next-enter-from,
  .step-prev-enter-from,
  .step-next-leave-to,
  .step-prev-leave-to {
    transform: none;
  }
}
</style>
